import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { getDeliveryCanTransactions, updateDeliveryCanTransaction, exportDeliveryCanTransactions } from '../../actions/deliveryCanTransaction';
import { getServiceableAreas } from '../../actions/serviceableArea';
import { 
    Container, 
    Row, 
    Col, 
    Card, 
    Form, 
    Button, 
    Table, 
    Badge, 
    Alert, 
    Spinner,
    Toast,
    ToastContainer,
    Pagination,
    Modal,
    InputGroup
} from 'react-bootstrap';
import { 
    Package, 
    User, 
    MapPin, 
    Clock, 
    Check, 
    AlertCircle, 
    Search,
    UserPlus,
    Truck,
    CheckCircle,
    Calendar,
    Phone,
    CreditCard,
    FileText,
    Trash2,
    XCircle,
    AlertTriangle,
    Filter,
    Download
} from 'lucide-react';
import "bootstrap/dist/css/bootstrap.min.css";

/**
 * DeliveryCanTransactions Component with Enhanced URL Query Parameter Support
 *
 * This component now supports extracting all query parameters from the URL:
 * - transactionId: Filter by transaction ID
 * - phone: Filter by phone number
 * - status: Filter by transaction status (pending, assigned, in-progress, delivered, cancelled, failed)
 * - agentName: Filter by agent name
 * - startDate: Filter by start date (YYYY-MM-DD)
 * - endDate: Filter by end date (YYYY-MM-DD)
 * - latest: Sort order (true/false)
 * - page: Page number for pagination
 *
 * Example URLs:
 * - /delivery-can-transactions?phone=1234567890
 * - /delivery-can-transactions?status=pending&startDate=2024-01-01&endDate=2024-01-31
 * - /delivery-can-transactions?agentName=John&latest=false&page=2
 */
const DeliveryCanTransactions = ({
    transactions,
    getDeliveryCanTransactions,
    updateDeliveryCanTransaction,
    exportDeliveryCanTransactions,
    getServiceableAreas,
    serviceableAreas,
    pageList,
    pages,
    activePage,
    loading,
    totalCount
}) => {
    // Get location from React Router v6 hook
    const location = useLocation();

    const initialState = {
        transactionId: '',
        startDate: '',
        endDate: '',
        phone: '',
        status: '',
        agentName: '',
        serviceableArea: '',
        latest: true
    };

    const [formData, setFormData] = useState(initialState);
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [queryString, setQueryString] = useState("page=1&latest=true");
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVariant, setToastVariant] = useState('success');
    const [exportLoading, setExportLoading] = useState(false);

    const [user, setUser] = useState("");

    const handleExportTransactions = async () => {
        setExportLoading(true);
        try {
            await exportDeliveryCanTransactions(queryString);
        } finally {
            setExportLoading(false);
        }
    };

    const showAlert = (message, variant = 'danger') => {
        setToastMessage(message);
        setToastVariant(variant);
        setShowToast(true);
    };

    // Function to extract query parameters from URL
    const getQueryParams = (search) => {
        const params = new URLSearchParams(search);
        const queryParams = {};
        // Extract all query parameters
        for (const [key, value] of params.entries()) {
            queryParams[key] = value;
        }
        return queryParams;
    };

    // Function to build query string from parameters
    const buildQueryString = (params) => {
        const queryParts = [];
        // Add default parameters
        queryParts.push(`page=${params.page || 1}`);
        queryParts.push(`latest=${params.latest || true}`);
        // Add other parameters if they exist
        if (params.user) queryParts.push(`user=${params.user}`);
        if (params.transactionId) queryParts.push(`transactionId=${params.transactionId}`);
        if (params.phone) queryParts.push(`phone=${params.phone}`);
        if (params.status) queryParts.push(`status=${params.status}`);
        if (params.agentName) queryParts.push(`agentName=${params.agentName}`);
        if (params.serviceableArea) queryParts.push(`serviceableArea=${params.serviceableArea}`);
        if (params.startDate) queryParts.push(`startDate=${params.startDate}`);
        if (params.endDate) queryParts.push(`endDate=${params.endDate}`);
        return queryParts.join('&');
    };

    useEffect(() => {
        document.title = 'Delivery Can Transactions';
        if(transactions === undefined){
            let query = queryString;
            if(location && location.search){
                // Extract all query parameters from URL
                const urlParams = getQueryParams(location.search);
                // Update form data with URL parameters
                const updatedFormData = {
                    ...formData,
                    transactionId: urlParams.transactionId || '',
                    phone: urlParams.phone || '',
                    status: urlParams.status || '',
                    agentName: urlParams.agentName || '',
                    serviceableArea: urlParams.serviceableArea || '',
                    startDate: urlParams.startDate || '',
                    endDate: urlParams.endDate || '',
                    latest: urlParams.latest !== undefined ? urlParams.latest === 'true' : true
                };
                setFormData(updatedFormData);
                // Set user if user parameter exists
                if (urlParams.user) {
                    setUser(urlParams.user);
                }
                // Build query string from URL parameters
                query = buildQueryString(urlParams);
                setQueryString(query);
            }
            getDeliveryCanTransactions(query);
        } else {
            processTransactionData(transactions);
            setCurrentPage(activePage || 1);
        }
    }, [location, transactions, getDeliveryCanTransactions]);

    useEffect(() => {
        getServiceableAreas();
    }, [getServiceableAreas]);

    const processTransactionData = (transactionData) => {
        const processedData = transactionData.map(transaction => ({
            ...transaction,
            requestTime: new Date(transaction.requestTime).toLocaleString(),
            rawRequestTime: new Date(transaction.requestTime),
            assignedTime: transaction.assignedTime ? new Date(transaction.assignedTime).toLocaleString() : null,
            rawAssignedTime: transaction.assignedTime ? new Date(transaction.assignedTime) : null,
            scheduledForDisplay: transaction.scheduledFor ? new Date(transaction.scheduledFor).toLocaleString() : null,
            userName: transaction.user ? transaction.user.name : "Deleted User",
            userPhone: transaction.user ? transaction.user.phone : "N/A",
            productName: transaction.product ? transaction.product.name : "Deleted Product",
            productBrand: transaction.product ? transaction.product.brand : "Unknown Brand",
            agentName: transaction.agent ? transaction.agent.name : "Not Assigned",
            sectorName: transaction.sector ? transaction.sector.name : "Not Assigned",
            serviceableAreaName: transaction.sector?.serviceableArea
                ? transaction.sector.serviceableArea.name
                : (transaction.superServiceableArea ? transaction.superServiceableArea.name : "Not Assigned"),
            deliveredTimeDisplay: transaction.deliveredTime ? new Date(transaction.deliveredTime).toLocaleString() : null
        }));

        // Sort by latest if needed
        if (formData.latest) {
            processedData.sort((a, b) => b.rawRequestTime - a.rawRequestTime);
        } else {
            processedData.sort((a, b) => a.rawRequestTime - b.rawRequestTime);
        }

        setData(processedData);
    };

    const {
        transactionId,
        startDate,
        endDate,
        phone,
        status,
        agentName,
        serviceableArea,
        latest
    } = formData;

    const onChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    }

    const onSubmit = e => {
        e.preventDefault();
        
        if (formData.startDate && formData.endDate) {
            const startDate = new Date(formData.startDate).getTime();
            const endDate = new Date(formData.endDate).getTime();
            if(endDate < startDate){
                showAlert("End date must be after start date");
                return;
            }
        }
        
        // Build query parameters object
        const queryParams = {
            page: 1,
            latest: formData.latest,
            transactionId: formData.transactionId || undefined,
            phone: formData.phone || undefined,
            status: formData.status || undefined,
            agentName: formData.agentName || undefined,
            serviceableArea: formData.serviceableArea || undefined,
            startDate: formData.startDate || undefined,
            endDate: formData.endDate || undefined
        };
        // Build query string from parameters
        const newQueryString = buildQueryString(queryParams);
        
        setQueryString(newQueryString);
        setCurrentPage(1);
        getDeliveryCanTransactions(newQueryString);
    };

    const handleSort = (field) => {
        const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortDirection(direction);
        
        const sortedData = [...data].sort((a, b) => {
            if (direction === 'asc') {
                return a[field] > b[field] ? 1 : -1;
            }
            return a[field] < b[field] ? 1 : -1;
        });
        setData(sortedData);
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return <i className="fas fa-sort text-muted ms-1"></i>;
        return sortDirection === 'asc' 
            ? <i className="fas fa-sort-up text-primary ms-1"></i>
            : <i className="fas fa-sort-down text-primary ms-1"></i>;
    };

    const clearFilters = () => {
        setFormData(initialState);
        setUser("");
        const queryParams = {
            page: 1,
            latest: true
        };
        const newQueryString = buildQueryString(queryParams);
        setQueryString(newQueryString);
        setCurrentPage(1);
        getDeliveryCanTransactions(newQueryString);
        showAlert("Filters cleared successfully!", "info");
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > pages) return;
        
        const newQueryString = queryString.replace(`page=${currentPage}`, `page=${page}`);
        setQueryString(newQueryString);
        setCurrentPage(page);
        getDeliveryCanTransactions(newQueryString);
    };

    const formatPhone = (phone) => {
        // Format phone number in IN +91 format
        if (phone && phone.length === 10) {
            return `+91 ${phone}`;
        }
        return phone;
    };



    const renderPagination = () => {
        // If no pages data from backend, don't show pagination
        if (!pages || pages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(pages, startPage + maxVisiblePages - 1);

        // First page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }

        // Page numbers
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Last page
        if (endPage < pages) {
            if (endPage < pages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            items.push(
                <Pagination.Item key={pages} onClick={() => handlePageChange(pages)}>
                    {pages}
                </Pagination.Item>
            );
        }

        return (
            <div className="p-3 border-top bg-light">
                <div className="d-flex justify-content-center mt-4">
                    <Pagination>
                        <Pagination.Prev
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                        />
                        {items}
                        <Pagination.Next
                            disabled={currentPage === pages}
                            onClick={() => handlePageChange(currentPage + 1)}
                        />
                    </Pagination>
                    <div className="ms-3 d-flex align-items-center">
                        <small className="text-muted">
                            Page {currentPage} of {pages} {pages > 1 ? `(${totalCount} total records)` : ''}
                        </small>
                    </div>
                </div>
            </div>
        );
    };

    const getStatusBadge = (status) => {
        switch(status?.toLowerCase()) {
            case 'pending':
                return <Badge bg="warning">Pending</Badge>;
            case 'assigned':
                return <Badge bg="info">Assigned</Badge>;
            case 'in-progress':
                return <Badge bg="primary">In Progress</Badge>;
            case 'scheduled':
                return <Badge bg="dark">Scheduled</Badge>;
            case 'delivered':
                return <Badge bg="success">Delivered</Badge>;
            case 'cancelled':
                return <Badge bg="secondary">Cancelled</Badge>;
            case 'failed':
                return <Badge bg="danger">Failed</Badge>;
            default:
                return <Badge bg="light" text="dark">{status || 'Unknown'}</Badge>;
        }
    };

    const renderTransactionTable = () => {
        if (!data || data.length === 0) {
            return (
                <Alert variant="info" className="text-center">
                    <i className="fas fa-info-circle me-2"></i>
                    No delivery transactions found. Try adjusting your filters.
                </Alert>
            );
        }

        return (
            <div className="table-responsive">
                <Table striped bordered hover className="mb-0">
                    <thead className="table-dark">
                        <tr>
                            <th
                                style={{ width: '10%' }}
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('transactionId')}
                            >
                                Transaction ID
                                {getSortIcon('transactionId')}
                            </th>
                            <th
                                style={{ width: '13%' }}
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('userName')}
                            >
                                Customer
                                {getSortIcon('userName')}
                            </th>
                            <th
                                style={{ width: '12%' }}
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('serviceableAreaName')}
                            >
                                Serviceable Area
                                {getSortIcon('serviceableAreaName')}
                            </th>
                            <th
                                style={{ width: '9%' }}
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('productName')}
                            >
                                Product
                                {getSortIcon('productName')}
                            </th>
                            <th style={{ width: '10%' }}>
                                Can Details
                            </th>
                            <th
                                style={{ width: '7%' }}
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('status')}
                            >
                                Status
                                {getSortIcon('status')}
                            </th>
                            <th
                                style={{ width: '9%' }}
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('agentName')}
                            >
                                Agent
                                {getSortIcon('agentName')}
                            </th>
                            <th
                                style={{ width: '7%' }}
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('depositUsed')}
                            >
                                Deposit
                                {getSortIcon('depositUsed')}
                            </th>
                            <th
                                style={{ width: '12%' }}
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('requestTime')}
                            >
                                Request Time
                                {getSortIcon('requestTime')}
                            </th>
                            <th
                                style={{ width: '11%' }}
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('deliveredTimeDisplay')}
                            >
                                Delivered Time
                                {getSortIcon('deliveredTimeDisplay')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((transaction, index) => (
                            <tr key={transaction._id || index}>
                                <td>
                                    <Link 
                                        to={`/inventory/v1.2/delivery-transactions/${transaction._id}`}
                                        className="text-decoration-none fw-bold text-primary transaction-link"
                                        title={`View transaction details: ${transaction.transactionId}`}
                                    >
                                        <span style={{maxWidth: '120px'}}>
                                            {transaction.transactionId}
                                        </span>
                                    </Link>
                                </td>
                                <td>
                                    <div>
                                        <div className={`fw-semibold ${transaction.userName === 'Deleted User' ? 'text-muted fst-italic' : ''}`}>
                                            {transaction.userName}
                                        </div>
                                        <small className="text-muted">
                                            <Phone size={12} className="me-1" />
                                            {formatPhone(transaction.userPhone)}
                                        </small>
                                    </div>
                                </td>
                                <td>
                                    <div>
                                        <div className="fw-semibold">{transaction.serviceableAreaName}</div>
                                        {transaction.sectorName !== 'Not Assigned' && (
                                            <small className="text-muted">{transaction.sectorName}</small>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div>
                                        <div className="fw-semibold">{transaction.productName}</div>
                                        <small className="text-muted">{transaction.productBrand}</small>
                                    </div>
                                </td>
                                <td>
                                    <div className="small">
                                        <div><strong>Filled:</strong> {transaction.filledCans}</div>
                                        <div><strong>Empty:</strong> {transaction.emptyCans}</div>
                                        {transaction.damageCans > 0 && (
                                            <div className="text-danger"><strong>Damaged:</strong> {transaction.damageCans}</div>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    {getStatusBadge(transaction.status)}
                                </td>
                                <td>
                                    <div className="fw-semibold">{transaction.agentName}</div>
                                </td>
                                <td>
                                    <Badge bg={transaction.depositUsed ? 'success' : 'secondary'}>
                                        {transaction.depositUsed ? 'Used' : 'Not Used'}
                                    </Badge>
                                </td>
                                <td>
                                    <small className="text-muted">
                                        <Calendar size={12} className="me-1" />
                                        {new Date(transaction.rawRequestTime).toLocaleDateString()}<br/>
                                        {new Date(transaction.rawRequestTime).toLocaleTimeString()}
                                    </small>
                                    {transaction.scheduledForDisplay && (
                                        <div>
                                            <small className="text-muted">
                                                Scheduled: {transaction.scheduledForDisplay}
                                            </small>
                                        </div>
                                    )}
                                </td>
                                <td>
                                    {transaction.deliveredTimeDisplay ? (
                                        <small className="text-muted">
                                            <Calendar size={12} className="me-1" />
                                            {new Date(transaction.deliveredTime).toLocaleDateString()}<br/>
                                            {new Date(transaction.deliveredTime).toLocaleTimeString()}
                                        </small>
                                    ) : (
                                        <small className="text-muted fst-italic">Not delivered</small>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <Container fluid className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="text-primary mb-0">
                    <Truck className="me-2" />
                    Delivery Can Transactions
                </h2>
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleExportTransactions}
                    disabled={exportLoading}
                    className="d-flex align-items-center"
                >
                    {exportLoading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-1" />
                            Exporting...
                        </>
                    ) : (
                        <>
                            <Download size={16} className="me-1" />
                            Export CSV
                        </>
                    )}
                </Button>
            </div>

            {/* Filters Card */}
            <Card className="shadow-sm mb-4">
                <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                        <Filter className="me-2" />
                        Search Filters
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={onSubmit}>
                        <Row className="g-3">
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <FileText className="me-2" size={16} />
                                        Transaction ID
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="transactionId"
                                        value={transactionId}
                                        onChange={onChange}
                                        placeholder="Enter transaction ID"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <Phone className="me-2" size={16} />
                                        Phone Number
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        value={phone}
                                        onChange={onChange}
                                        placeholder="Enter phone number"
                                        maxLength={10}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <AlertCircle className="me-2" size={16} />
                                        Status
                                    </Form.Label>
                                    <Form.Select
                                        name="status"
                                        value={status}
                                        onChange={onChange}
                                    >
                                        <option value="">All Statuses</option>
                                        <option value="pending">Pending</option>
                                        <option value="assigned">Assigned</option>
                                        <option value="in-progress">In Progress</option>
                                        <option value="scheduled">Scheduled</option>
                                        <option value="delivered">Delivered</option>
                                        <option value="cancelled">Cancelled</option>
                                        <option value="failed">Failed</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <User className="me-2" size={16} />
                                        Agent Name
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="agentName"
                                        value={agentName}
                                        onChange={onChange}
                                        placeholder="Enter agent name"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <Calendar className="me-2" size={16} />
                                        Start Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="startDate"
                                        value={startDate}
                                        onChange={onChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <Calendar className="me-2" size={16} />
                                        End Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="endDate"
                                        value={endDate}
                                        onChange={onChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-sort me-2"></i>
                                        Sort Order
                                    </Form.Label>
                                    <Form.Select
                                        name="latest"
                                        value={latest}
                                        onChange={onChange}
                                    >
                                        <option value="true">Newest First</option>
                                        <option value="false">Oldest First</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <MapPin className="me-2" size={16} />
                                        Serviceable Area
                                    </Form.Label>
                                    <Form.Select
                                        name="serviceableArea"
                                        value={serviceableArea}
                                        onChange={onChange}
                                    >
                                        <option value="">All Serviceable Areas</option>
                                        {serviceableAreas && serviceableAreas.map(area => (
                                            <option key={area._id} value={area._id}>
                                                {area.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={12} className="d-flex align-items-end">
                                <div className="d-flex gap-2 w-100">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={loading}
                                        className="flex-fill"
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Searching...
                                            </>
                                        ) : (
                                            <>
                                                <Search className="me-2" size={16} />
                                                Apply Filters
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline-secondary"
                                        onClick={clearFilters}
                                        disabled={loading}
                                    >
                                        <XCircle className="me-2" size={16} />
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* Summary Cards */}
            {data.length > 0 && (
                <Row className="g-3 mb-4 justify-content-center">
                    <Col lg={2} md={4} sm={6}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <FileText className="text-primary fa-2x mb-2" size={32} />
                                <h5 className="card-title">Total Records</h5>
                                <h3 className="text-primary">{data.length}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={2} md={4} sm={6}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <AlertCircle className="text-warning fa-2x mb-2" size={32} />
                                <h5 className="card-title">Pending</h5>
                                <h3 className="text-warning">
                                    {data.filter(row => row.status?.toLowerCase() === 'pending').length}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={2} md={4} sm={6}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <Truck className="text-info fa-2x mb-2" size={32} />
                                <h5 className="card-title">Assigned</h5>
                                <h3 className="text-info">
                                    {data.filter(row => row.status?.toLowerCase() === 'assigned').length}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={2} md={4} sm={6}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <CheckCircle className="text-success fa-2x mb-2" size={32} />
                                <h5 className="card-title">Delivered</h5>
                                <h3 className="text-success">
                                    {data.filter(row => row.status?.toLowerCase() === 'delivered').length}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={2} md={4} sm={6}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <XCircle className="text-danger fa-2x mb-2" size={32} />
                                <h5 className="card-title">Failed/Cancelled</h5>
                                <h3 className="text-danger">
                                    {data.filter(row => ['failed', 'cancelled'].includes(row.status?.toLowerCase())).length}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Results Card */}
            <Card className="shadow-sm">
                <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="fas fa-table me-2"></i>
                        Delivery Transaction Records
                    </h5>
                    <div className="d-flex align-items-center gap-3">
                        {pages > 1 && (
                            <small className="text-light">
                                Page {currentPage} of {pages}
                            </small>
                        )}
                        {data.length > 0 && (
                            <Badge bg="light" text="dark">
                                {data.length} record{data.length !== 1 ? 's' : ''}
                            </Badge>
                        )}
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <div className="mt-2">Loading delivery transaction data...</div>
                        </div>
                    ) : (
                        <>
                            {renderTransactionTable()}
                            {/* Pagination */}
                            {renderPagination()}
                            
                            {/* Fallback pagination info when no pagination data from backend */}
                            {!pages && data.length > 0 && (
                                <div className="p-3 border-top bg-light text-center">
                                    <small className="text-muted">
                                        Showing {data.length} records
                                        {data.length >= 50 && " (pagination data not available)"}
                                    </small>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>



            {/* Toast Notifications */}
            <ToastContainer position="top-end" className="p-3">
                <Toast
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    delay={5000}
                    autohide
                    bg={toastVariant}
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toastVariant === 'success' ? 'Success' : toastVariant === 'info' ? 'Info' : 'Alert'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className={toastVariant === 'danger' ? '' : 'text-white'}>
                        {toastMessage}
                    </Toast.Body>
                </Toast>
            </ToastContainer>

            <style>{`
                .cursor-pointer {
                    cursor: pointer;
                }
                .user-select-none {
                    user-select: none;
                }
                .table th:hover {
                    background-color: #495057 !important;
                }
                .transaction-link:hover {
                    text-decoration: underline !important;
                    color: #0d6efd !important;
                }
                .transaction-link:hover span {
                    color: #0d6efd !important;
                }
            `}</style>
        </Container>
    )
}

DeliveryCanTransactions.propTypes = {
    getDeliveryCanTransactions: PropTypes.func.isRequired,
    updateDeliveryCanTransaction: PropTypes.func.isRequired,
    exportDeliveryCanTransactions: PropTypes.func.isRequired,
    getServiceableAreas: PropTypes.func.isRequired,
    serviceableAreas: PropTypes.array,
    transactions: PropTypes.array,
    pages: PropTypes.number,
    activePage: PropTypes.number,
    pageList: PropTypes.array,
    loading: PropTypes.bool,
    totalCount: PropTypes.number
}

const mapStateToProps = state => ({
    transactions: state.deliveryCanTransaction.transactions,
    pages: state.deliveryCanTransaction.pages,
    pageList: state.deliveryCanTransaction.pageList,
    activePage: state.deliveryCanTransaction.activePage,
    loading: state.deliveryCanTransaction.loading,
    totalCount: state.deliveryCanTransaction.totalCount,
    serviceableAreas: state.serviceableArea.serviceableAreas
})

export default connect( mapStateToProps, {
    getDeliveryCanTransactions,
    updateDeliveryCanTransaction,
    exportDeliveryCanTransactions,
    getServiceableAreas
})(DeliveryCanTransactions);
